import { useState } from 'react'

import BusinessFormTitle from 'src/features/business-create/components/business-form-title'
import Breadcrumbs from 'src/features/business-create/components/ui/breadcrumbs'
import useFormattedFieldValue from 'src/features/business-create/hooks/use-formatted-field-value'
import EditInfoModal from 'src/features/business-create/modals/edit-info-modal'
import type { BusinessInfo } from 'src/features/business-create/schemas/business-create'
import {
  BusinessInfoFieldsPerType,
  BusinessInfoLabels,
  BusinessInfoTypeLabels,
} from 'src/features/business-create/schemas/business-create'
import useBusinessAdditionalInfoStore from 'src/features/business-create/store/business-additional-info-store'
import { useBusinessCreateStore } from 'src/features/business-create/store/business-create-store'

const EditPencilIcon = ({ className }: { className?: string }) => {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width="17"
      height="16"
      viewBox="0 0 17 16"
      fill="none"
    >
      <path
        d="M9.89439 2.66797L1.07891 11.4861C1.03456 11.5305 1.00254 11.5867 0.987289 11.6471L0.0101776 15.5699C-0.0190506 15.688 0.015642 15.8136 0.101802 15.8997C0.166993 15.9649 0.255821 16.001 0.346683 16.001C0.374513 16.001 0.402979 15.9976 0.430682 15.9906L4.3526 15.0132C4.41372 14.9979 4.46926 14.966 4.51361 14.9216L13.3298 6.1042L9.89439 2.66797Z"
        fill="currentColor"
      />
      <path
        d="M15.5085 1.47329L14.5272 0.491763C13.8713 -0.164239 12.7282 -0.163603 12.0731 0.491763L10.8711 1.69407L14.3064 5.13017L15.5085 3.92786C15.8361 3.60031 16.0165 3.16433 16.0165 2.70064C16.0165 2.23695 15.8361 1.80097 15.5085 1.47329Z"
        fill="currentColor"
      />
    </svg>
  )
}

const EditInfoBox = ({
  infoField,
  onPress,
}: {
  infoField: keyof BusinessInfo
  onPress: ({ key, value }: { key: keyof BusinessInfo; value: any }) => void
}) => {
  const { formData } = useBusinessCreateStore()

  const title = BusinessInfoLabels[infoField]
  const value = formData[infoField] ?? null
  const { value: formattedValue } = useFormattedFieldValue(infoField, value)

  return (
    <button
      className="flex w-[500px] flex-row items-center justify-between gap-4 rounded-[15px] bg-accent px-6 py-4"
      onClick={() => onPress({ key: infoField, value })}
    >
      <div className="flex flex-col justify-center gap-1 text-left">
        <p className="text-base font-semibold leading-normal text-primary">{title}</p>
        <p className="text-sm font-medium leading-normal text-muted">{formattedValue}</p>
      </div>

      <EditPencilIcon className="size-4 text-primary" />
    </button>
  )
}

const AdditionalInfoEditInfoTypeStep = ({ onBack }: { onBack: () => void }) => {
  const { formData, setFormData } = useBusinessCreateStore()
  const { selectedInfoType } = useBusinessAdditionalInfoStore()
  const [editingInfoField, setEditingInfoField] = useState<{ key: keyof BusinessInfo; value: any } | null>(null)

  const onSave = (value: any) => {
    if (!editingInfoField) return

    setFormData({
      ...formData,
      [editingInfoField.key]: value,
    })
    setEditingInfoField(null)
  }

  if (!selectedInfoType) {
    return null
  }

  const infoFields = BusinessInfoFieldsPerType[selectedInfoType]

  return (
    <>
      <div className="flex size-full flex-col items-center gap-10 py-10">
        <div className="flex flex-col items-center gap-1 text-center">
          <Breadcrumbs
            breadcrumbs={[
              { label: 'Additional info', onClick: onBack },
              { label: BusinessInfoTypeLabels[selectedInfoType] },
            ]}
            className="mb-4"
          />
          <BusinessFormTitle>{BusinessInfoTypeLabels[selectedInfoType]}</BusinessFormTitle>
          <p className="text-lg font-medium leading-normal text-muted">
            Add any and all information that can help Lynk grade this business for you.
          </p>
        </div>
        <div className="flex flex-col gap-4">
          {infoFields.map((infoField) => (
            <EditInfoBox key={infoField} infoField={infoField} onPress={setEditingInfoField} />
          ))}
        </div>
      </div>
      <EditInfoModal
        open={!!editingInfoField}
        onClose={() => setEditingInfoField(null)}
        infoField={editingInfoField?.key ?? null}
        initialValue={editingInfoField?.value ?? null}
        onSave={onSave}
      />
    </>
  )
}

export default AdditionalInfoEditInfoTypeStep
